import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const SNOOZE_KEY = "tx_install_snoozed_until";
const INSTALLED_KEY = "tx_install_done";
const SNOOZE_DAYS = 14;

/* Reading storage can throw in private modes — an install nudge must never be
   able to break the page it is nudging from. */
const readStore = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const writeStore = (key, value) => {
  try { localStorage.setItem(key, value); } catch { /* non-fatal */ }
};

const isSuppressed = () => {
  if (readStore(INSTALLED_KEY) === "1") return true;
  const until = Number(readStore(SNOOZE_KEY) || 0);
  return Number.isFinite(until) && Date.now() < until;
};

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      // The dismissal has to be checked here, not just at mount: several
      // routes navigate with a full page load, which re-fires this event and
      // was what made the prompt feel like it came back on every page.
      if (isSuppressed()) return;
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installed = () => {
      writeStore(INSTALLED_KEY, "1");
      setShow(false);
    };
    window.addEventListener("appinstalled", installed);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  const snooze = () => {
    writeStore(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 86400000));
    setShow(false);
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    // Close on BOTH outcomes. Previously only "accepted" hid the banner, so
    // cancelling Chrome's dialog left it on screen with a dead Install button
    // (the deferred event is single-use and is now spent).
    if (outcome === "accepted") writeStore(INSTALLED_KEY, "1");
    else snooze();
    setDeferredPrompt(null);
    setShow(false);
  };

  // Ops working in the admin dashboard have no use for an install nudge. Nor
  // does a first-time visitor on the landing page or an ad's landing page:
  // there the banner sat on top of the booking form / CTA before they had
  // done anything. It still shows on the pages people reach after booking or
  // tracking (waitlists, dashboard), where installing actually helps.
  const isEntryPage = pathname === "/" || pathname === "/login" || pathname.startsWith("/travel/");
  if (!show || pathname.startsWith("/admin") || isEntryPage) return null;

  return (
    <div style={styles.banner}>
      <div style={styles.text}>
        Install <strong>TurantX</strong> for faster access.
      </div>
      <div style={styles.buttons}>
        <button onClick={handleInstall} style={styles.install}>Install</button>
        <button onClick={snooze} style={styles.dismiss} aria-label="Not now">✕</button>
      </div>
    </div>
  );
}

const styles = {
  banner: {
    position: "fixed",
    bottom: "calc(80px + env(safe-area-inset-bottom, 0px))",
    left: "12px",
    right: "12px",
    background: "#1a1a2e",
    color: "#fff",
    borderRadius: "14px",
    padding: "14px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    zIndex: 9999,
    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
    gap: "12px",
  },
  text: { fontSize: "14px", flex: 1 },
  buttons: { display: "flex", gap: "8px", alignItems: "center" },
  install: {
    background: "#f97316",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "8px 16px",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "14px",
  },
  dismiss: {
    background: "transparent",
    color: "#aaa",
    border: "none",
    fontSize: "18px",
    cursor: "pointer",
    padding: "4px",
  },
};
